import axios from 'axios'
import Cookies from 'js-cookie'

const fetchCurrentUser = async ({ commit, rootState }) => {
  await axios.get(`${rootState.baseURL}/user/`, {
  })
  .then((res) => {
    if (res) {
      commit('setCurrentUserData', res.data.data)
    }
  })
}

const fetchUserData = async ({ commit, rootState, dispatch }, payload) => {
  await axios.get(`${rootState.baseURL}/user/${payload}`, {
  })
  .then((res) => {
    if (res) {
      commit('setUserData', res.data.data)
    }
  })
}

const impersonateUser = async ({ commit, rootState, dispatch }, payload) => {
  await axios.post(`${rootState.baseURL}/impersonate/${payload}`, {})
  .then((res) => {
    if (res) {
      commit('updateUserToken', res.data.data.userToken, { root: true })
      Cookies.set('refreshToken', res.data.data.refreshToken, { expires: 30, domain: process.env.VUE_APP_COOKIE_URL })
      Cookies.set('expiresAt', res.data.data.expiresAt, { expires: 30, domain: process.env.VUE_APP_COOKIE_URL })
      window.location = process.env.VUE_APP_DASHBOARD
    }
  })
}

const resetUserPassword = ({ rootState, dispatch }, payload) => {
  axios.post(`${rootState.baseURL}/forgotPassword/${payload.email}`, { override: false })
  .then((res) => {
    if (res) {
      const message = { type: 'success', text: 'Password reset email has been sent' }
      dispatch('updateMessages', message, { root: true })
    }
  })
}

const updateUser = ({ rootState, dispatch }, payload) => {
  const uninterceptedAxiosInstance = axios.create({ headers: { Authorization: rootState.userToken } })
  return uninterceptedAxiosInstance.put(`${rootState.baseURL}/user/${payload.userId}`, payload.body, {})
}

const fetchUserNotificationSettings = ({ commit, rootState, dispatch }, payload) => {
  return axios.get(`${rootState.baseURL}/user/notification/settings/${payload}`)
  .then(res => {
    if (res) {
      commit('setUserNotificationSettings', res.data.data)
    }
  })
}

const updateUserNotificationSettings = async ({ commit, rootState, dispatch }, { updated, value, userId, orgId }) => {
  const uninterceptedAxiosInstance = axios.create({ headers: { Authorization: rootState.userToken } })
  return await uninterceptedAxiosInstance.put(`${rootState.baseURL}/user/notification/settings/${userId}`, {
    [updated]: value,
    organizationID: orgId,
  })
    .then(res => {
      if (res) {
        commit('setUserNotificationSettings', res.data.data)
      }
    })
    .catch(err => {
      const toastPayload = { type: 'error', text: 'We were unable to update this account\'s settings. Please try again.' }
      dispatch('updateMessages', toastPayload, { root: true })
      throw err
    })
}

const updateUserReportSettings = async ({ commit, rootState, dispatch }, payload) => {
  const uninterceptedAxiosInstance = axios.create({ headers: { Authorization: rootState.userToken } })
  await uninterceptedAxiosInstance.put(`${rootState.baseURL}/user/`)
    .then(res => commit('setUserReportSettings', res.data.data))
    .catch(err => {
      const toastPayload = { type: 'error', text: 'We were unable to update this account\'s settings. Please try again.' }
      dispatch('updateMessages', toastPayload, { root: true })
      throw err
    })
}

const fetchNextUser = async ({ rootState, commit, dispatch }, payload) => {
  await axios.post(`${rootState.baseURL}/user/list`, payload, {
  })
    .then((res) => {
      if (res) {
        commit('updateCurrentOrgUser', res.data.data[0])
      }
    })
}

/**
 * @description fetch the list of properties, marking the properties
 * that the provided user can access on the specified organization.
 * First we fetch the properties list for the logged-in user
 * Then we fetch the properties list, filtered by the userId parameter.
 * To ensure the pages align between the calls, we issue the second call
 * with the list of properties returned from the first as a filter,
 * and update the pagination to encompass all properties from the first.
 */
const fetchEditPropertyPermissionsList = async ({ rootState, commit }, { userId, listPayload }) => {
  const res = await axios.post(`${rootState.baseURL}/properties/list`, listPayload)
  const properties = (res.data.data || [])
  const propertiesPagination = res.data.pagination
  if (!properties.length) { // if no properties, no need to fetch permissions
    return {
      data: {
        data: properties,
        pagination: propertiesPagination,
      },
    }
  }
  const permissionsPayload = {
    filter: [
      { key: 'user_id', operator: '=', value: userId },
      { key: 'id', operator: 'in', value: properties.map((property) => property.id).join(',') },
    ],
    pagination: {
      perPage: properties.length,
      pageNum: 1,
    },
  }
  return axios.post(`${rootState.baseURL}/properties/list`, permissionsPayload)
    .then((res) => {
      if (res) {
        const propertiesWithAccess = (res.data.data || []).map((property) => property.id)
        return {
          data: {
            data: properties.map((property) => ({ canAccess: propertiesWithAccess.includes(property.id), ...property })),
            pagination: propertiesPagination,
          },
        }
      }
    })
}

const updateUserRole = async ({ commit, rootState, dispatch }, { userId, orgId, orgName, role }) => {
  await axios.put(`${rootState.baseURL}/user/orgRole/${userId}`, {
    id: orgId,
    name: orgName,
    role: role,
  })
    .then(res => {
      if (res) {
        const payload = { type: 'success', text: res.data.detail }
        dispatch('updateMessages', payload, { root: true })
        commit('setUserData', res.data.data)
      }
    })
}

const updateUserPropertySettings = ({ dispatch, rootState }, payload) => {
 axios.put(`${rootState.baseURL}/user/notification/properties/${payload.userId}`, payload.body)
 .then((res) => {
   if (res.status === 200) {
    const payload = { type: 'success', text: 'Notification settings updated!', unread: true }
    dispatch('updateMessages', payload, { root: true })
   }
 })
}

const updateUserPropertyAccess = async ({ rootState, dispatch }, payload) => {
  await axios.post(`${rootState.baseURL}/properties/user/${payload.userId}`, payload.body)
    .then(res => {
      if (res === undefined) return
      const payload = { type: 'success', text: 'Property successfully added!', unread: true }
      dispatch('updateMessages', payload, { root: true })
    })
}

const deleteUserPropertyAccess = async ({ rootState, dispatch }, payload) => {
  await axios.delete(`${rootState.baseURL}/properties/user/${payload.userId}`, { data: payload.body })
    .then(res => {
      if (res === undefined) return
      const payload = { type: 'success', text: 'Property successfully deleted!', unread: true }
      dispatch('updateMessages', payload, { root: true })
    })
}

const fetchUserTimezone = async ({ rootState, commit }, payload) => {
  await axios.get(`${rootState.baseURL}/reports/user/${payload.userId}/preference`)
  .then((res) => {
    commit('setUserTimezone', res.data.data?.timeZone ?? null)
  })
}

const createUserTimezone = async ({ rootState, commit }, payload) => {
  await axios.post(`${rootState.baseURL}/reports/user/preference`, { timeZone: payload.userTimezone, UserID: payload.userId, orgs: payload.orgs })
  .then((res) => {
    commit('setUserTimezone', res.data.data?.timeZone ?? null)
  })
}

const updateUserTimezone = async ({ rootState, commit }, payload) => {
  await axios.put(`${rootState.baseURL}/reports/user/${payload.userId}/preference`, { timeZone: payload.userTimezone })
  .then((res) => {
    commit('setUserTimezone', res.data.data?.timeZone ?? null)
  })
}

export default {
  fetchCurrentUser,
  fetchUserData,
  impersonateUser,
  resetUserPassword,
  updateUser,
  fetchUserNotificationSettings,
  updateUserNotificationSettings,
  updateUserReportSettings,
  fetchEditPropertyPermissionsList,
  updateUserRole,
  updateUserPropertySettings,
  updateUserPropertyAccess,
  deleteUserPropertyAccess,
  fetchNextUser,
  fetchUserTimezone,
  createUserTimezone,
  updateUserTimezone,
}
